// src/client/renderer/character/preview-rig.ts
//
// Plan 24a-consumable preview rig (Plan 13, Task 6.1).
//
// Composes EVERYTHING from prior Plan 13 tasks into a single API surface:
//   - assembler.ts:assembleCharacter (Task 1.1)
//   - color-swap.ts:applyBodyColor   (Task 4.1)
//   - head-swap.ts:swapHead          (Task 2.1)
//   - eye-sprite.ts:attachEyeSprite  (Task 2.2)
//   - cape.ts:attachCape             (Task 3.1)
//   - cape-shader-params.ts:applyCapeParams (Task 3.2 — invoked inside attachCape)
//   - outline-binding.ts:bindCharacterOutline + enableCharacterShadows (Task 5.1)
//
// Per-eye texture (NOT sprite-sheet): per Task 2.2 coordinator-authorized scope
// adaptation, the manifest ships 8 separate per-eye PNGs. createPreviewRig
// therefore takes `eyeTextures: Partial<Record<EyeId, THREE.Texture>>` and looks
// up `eyeTextures[customization.eyes]` on each composition pass. A missing
// entry throws a clear error — the caller (asset loader) MUST pre-load every
// eye id that may be selected.
//
// Outline lifecycle: bindCharacterOutline returns a caller-managed
// `{ dispose(): void }` handle (Task 5.1 plan contract — NOT slot-based).
// We track that handle in a closure and dispose it (a) before each
// applyCustomization re-run and (b) once during the preview rig's final
// dispose() — BEFORE delegating to the composed rig.dispose() (which itself
// chains the slot-disposers for head/eye/cape).
//
// Anchors contract: `rig.anchors` is mutated IN PLACE on every
// applyCustomization re-run, so callers holding a reference (e.g. Plan 24a's
// nameplate sprite mount logic) see the update without re-reading. The pure
// helpers `computeNameplateAnchor` and `computeSpeechBubbleAnchor` always
// return a FRESH Vector3 (no aliasing) so callers that prefer immutable reads
// can re-call them after each frame/state change.

import * as THREE from 'three';
import type {
  CapeId,
  CharacterCustomization,
  CharacterRig,
  EyeExpression,
  EyeId,
  HeadId,
} from './types';
import { assembleCharacter } from './assembler';
import { applyBodyColor } from './color-swap';
import { swapHead } from './head-swap';
import { attachEyeMeshes, disposeEyeMeshes } from './eye-sprite';
import { attachCape, disposeCape } from './cape';
import { bindCharacterOutline, enableCharacterShadows } from './outline-binding';
import type { EyeTextureStore } from './eye-texture-store';

/** World-space anchor positions for Plan 24a/24b UI sprite mounting (NOT scene-graph children). */
export interface CharacterAnchors {
  /** Plan 24a: <shape-icon> <name> nameplate sprite anchor. */
  nameplate: THREE.Vector3;
  /** Plan 24b: emote bubble sprite anchor; offset above nameplate. */
  speechBubble: THREE.Vector3;
}

/** Plan 24a-consumable preview rig (color + head + eyes + cape + outline + anchors). Caller mounts root. */
export interface CharacterPreviewRig extends CharacterRig {
  /** Mutated in place by applyCustomization — callers holding the reference see updates. */
  anchors: CharacterAnchors;
  /** Re-runs the full composition with the new customization (color → head → eye → cape → outline → anchors). */
  applyCustomization(c: CharacterCustomization): void;
  /** Client-only cosmetic expression swap. Falls back to base when an expression texture is unavailable. */
  setEyeExpression(expression: EyeExpression): void;
}

/** Vertical clearance above head's bounding-box top for the nameplate anchor. */
const NAMEPLATE_OFFSET_ABOVE_HEAD = 0.3;

/** Vertical separation between nameplate and speech-bubble (per spec §9 + roadmap line 271). */
const SPEECH_BUBBLE_OFFSET_ABOVE_NAMEPLATE = 0.42;

/** Fallback offset above body when no head mesh is attached to the rig. */
const NAMEPLATE_OFFSET_ABOVE_BODY = 0.3;

/**
 * Build a Plan 24a-consumable preview rig from pre-loaded GLB scenes + textures.
 *
 * Composition order (Plan 13 Phase-by-phase dependency):
 *   1. assembleCharacter        — body mesh + dispose chain
 *   2. applyBodyColor           — color tint on body material
 *   3. swapHead                 — graceful-degrade aware head attach
 *   4. attachEyeSprite          — per-eye texture on Sprite (NOT sprite-sheet)
 *   5. attachCape (if !== 'none') — cape mesh + per-cape shader params
 *   6. bindCharacterOutline     — inverted-hull outline sibling on rig.root
 *   7. enableCharacterShadows   — castShadow=true on every Object3D
 *   8. compute anchors          — nameplate + speech bubble (in-place mutation slot)
 *
 * `applyCustomization(c)` re-runs steps 2-7 (without recreating the body) and
 * recomputes the anchors. The outline handle is disposed and re-created on
 * every call so the outline color tracks the new player color.
 *
 * @throws Error if `eyeTextures[initial.eyes]` is missing — caller must pre-load.
 * Missing cape textures degrade to no cape; remote players and bots can reference
 * cosmetics that were not part of the local boot preload.
 */
export function createPreviewRig(args: {
  bodyGltfScene: THREE.Group;
  bodyAnimations?: readonly THREE.AnimationClip[];
  headGltfScenes: Partial<Record<HeadId, THREE.Group>>;
  fallbackHeadGltfScene: THREE.Group;
  manifestEntries?: Partial<Record<HeadId, { placeholder: boolean }>>;
  eyeTextures: Partial<Record<EyeId, THREE.Texture>>;
  eyeTextureStore?: EyeTextureStore;
  capeTextures: Partial<Record<Exclude<CapeId, 'none'>, THREE.Texture>>;
  initial: CharacterCustomization;
}): CharacterPreviewRig {
  const {
    bodyGltfScene,
    bodyAnimations,
    headGltfScenes,
    fallbackHeadGltfScene,
    manifestEntries,
    eyeTextures,
    eyeTextureStore,
    capeTextures,
    initial,
  } = args;

  // ── 1. Assemble core rig (body + slot reservations) ─────────────────────────
  const rig = assembleCharacter({
    bodyGltfScene,
    customization: initial,
    animations: bodyAnimations,
  });

  // ── Anchors slot — populated after the first composition pass; mutated in place
  //    on every applyCustomization re-run so external references stay live. ────
  const anchors: CharacterAnchors = {
    nameplate: new THREE.Vector3(),
    speechBubble: new THREE.Vector3(),
  };

  // ── Outline handle slot — caller-managed per Task 5.1 plan contract. ───────
  let outlineHandle: { dispose(): void } | null = null;
  let eyeRetainRelease: (() => void) | null = null;
  let currentCustomization = initial;
  let currentEyeExpression: EyeExpression = 'base';
  let disposed = false;

  function requestEyeAttachment(c: CharacterCustomization, expression: EyeExpression): void {
    const generation = (rig._eyeGeneration ?? 0) + 1;
    rig._eyeGeneration = generation;
    const requestedHead = c.head;
    const requestedEyes = c.eyes;
    const baseTexture = eyeTextures[c.eyes] ?? null;
    const texturePromise =
      expression === 'base'
        ? Promise.resolve(baseTexture)
        : (eyeTextureStore?.resolve(c.eyes, expression) ?? Promise.resolve(baseTexture));

    void texturePromise.then((resolvedTexture) => {
      if (disposed) return;
      if (rig._eyeGeneration !== generation) return;
      if (
        currentCustomization.head !== requestedHead ||
        currentCustomization.eyes !== requestedEyes
      )
        return;
      if (currentEyeExpression !== expression) return;

      const texture = resolvedTexture ?? baseTexture;
      if (texture == null) {
        disposeEyeMeshes(rig);
        return;
      }

      eyeRetainRelease?.();
      const renderedExpression =
        expression !== 'base' && texture !== baseTexture ? expression : ('base' as EyeExpression);
      eyeRetainRelease = eyeTextureStore?.retain(c.eyes, renderedExpression) ?? null;
      attachEyeMeshesWithRootScaleNeutralized({
        rig,
        headId: c.head,
        eyeId: c.eyes,
        expression: renderedExpression,
        eyeTexture: texture,
      });
      enableCharacterShadows(rig);
    });
  }

  // ── compose: re-runs all per-customization steps. Used by initial build
  //    and by applyCustomization. Disposes the prior outline handle first so
  //    the outline color always reflects the latest selected color. ───────────
  function compose(c: CharacterCustomization): void {
    currentCustomization = c;
    // 2. Body color (in-place material mutation; identity preserved)
    applyBodyColor(rig, c.color);

    // 3. Head — graceful-degrade aware (swapHead handles all 4 trigger conditions)
    const headGltfScene = headGltfScenes[c.head] ?? null;
    swapHead({
      rig,
      headId: c.head,
      headGltfScene,
      fallbackGltfScene: fallbackHeadGltfScene,
      manifestEntry: manifestEntries?.[c.head],
    });

    // 4. Eyes — head-local decals using EYE_TUNING. Non-base expressions lazy-load
    //    and fall back to the eager base texture when unavailable/placeholder.
    requestEyeAttachment(c, currentEyeExpression);

    // 5. Cape — sentinel 'none' detaches; missing textures degrade to no cape.
    if (c.cape === 'none') {
      // disposeCape is idempotent — a no-op when no cape is attached.
      disposeCape(rig);
    } else {
      const capeTexture = capeTextures[c.cape];
      if (capeTexture === undefined) {
        disposeCape(rig);
      } else {
        attachCape({ rig, capeId: c.cape, capeTexture });
      }
    }

    // 6. Outline — caller-managed handle. Dispose prior, bind new (color tracks c.color).
    if (outlineHandle !== null) {
      outlineHandle.dispose();
      outlineHandle = null;
    }
    outlineHandle = bindCharacterOutline({ rig, colorId: c.color });

    // 7. Shadows — enable on EVERY object in the rig (idempotent: setting castShadow
    //    repeatedly is harmless; new children added on subsequent re-composes inherit
    //    castShadow=true via this traversal).
    enableCharacterShadows(rig);

    // 8. Anchors — mutate in place so external references stay live.
    anchors.nameplate.copy(computeNameplateAnchor(rig));
    anchors.speechBubble.copy(computeSpeechBubbleAnchor(rig));
  }

  // ── First composition pass ──────────────────────────────────────────────────
  compose(initial);

  // ── Build the preview rig wrapper. dispose() releases the outline handle
  //    BEFORE delegating to the composed rig.dispose() (which chains the
  //    slot-disposers for head/eye/cape via assembler.ts). Order matters
  //    because the outline sibling shares geometry with rig.bodyMesh — disposing
  //    it AFTER bodyMesh.geometry.dispose() would call .removeFromParent() on
  //    a detached sibling, harmless but wasteful. ─────────────────────────────
  const composedDispose = rig.dispose;
  function previewDispose(): void {
    if (disposed) return;
    disposed = true;
    if (outlineHandle !== null) {
      outlineHandle.dispose();
      outlineHandle = null;
    }
    eyeRetainRelease?.();
    eyeRetainRelease = null;
    composedDispose();
  }

  function applyCustomization(c: CharacterCustomization): void {
    if (disposed) {
      throw new Error('[CharacterPreviewRig] applyCustomization called after dispose().');
    }
    compose(c);
  }

  function setEyeExpression(expression: EyeExpression): void {
    if (disposed) {
      throw new Error('[CharacterPreviewRig] setEyeExpression called after dispose().');
    }
    if (currentEyeExpression === expression) return;
    currentEyeExpression = expression;
    requestEyeAttachment(currentCustomization, expression);
  }

  const previewRig: CharacterPreviewRig = {
    root: rig.root,
    visualRoot: rig.visualRoot,
    modelRoot: rig.modelRoot,
    bodyMesh: rig.bodyMesh,
    bodySkinnedMesh: rig.bodySkinnedMesh,
    renderMeshes: rig.renderMeshes,
    colorableMeshes: rig.colorableMeshes,
    mixer: rig.mixer,
    actions: rig.actions,
    tick: rig.tick,
    playAnimation: rig.playAnimation,
    resetSimulation: rig.resetSimulation,
    resolveSocket: rig.resolveSocket,
    dispose: previewDispose,
    anchors,
    applyCustomization,
    setEyeExpression,
    // Forward the slot trackers via getters so external code reads the live
    // values from the underlying CharacterRig (which the slot-attach helpers
    // mutate). This is critical because attachCape/swapHead/attachEyeSprite
    // mutate `rig._headMesh`, `rig._eyeSprite`, `rig._capeMesh` directly.
    get _headRoot(): THREE.Object3D | null | undefined {
      return rig._headRoot;
    },
    get _headMesh(): THREE.Mesh | null | undefined {
      return rig._headMesh;
    },
    get _eyeSprite(): THREE.Sprite | null | undefined {
      return rig._eyeSprite;
    },
    get _eyeGroup(): THREE.Group | null | undefined {
      return rig._eyeGroup;
    },
    get _eyeGeneration(): number | undefined {
      return rig._eyeGeneration;
    },
    get _eyeExpression(): EyeExpression | undefined {
      return rig._eyeExpression;
    },
    get _capeMesh(): THREE.Mesh | null | undefined {
      return rig._capeMesh;
    },
    get _capeSimulation() {
      return rig._capeSimulation;
    },
  };

  return previewRig;
}

function attachEyeMeshesWithRootScaleNeutralized(args: {
  rig: CharacterRig;
  headId: HeadId;
  eyeId: EyeId;
  expression: EyeExpression;
  eyeTexture: THREE.Texture;
}): void {
  const rootScale = args.rig.root.scale.clone();
  const isRootScaled =
    Math.abs(rootScale.x - 1) > 1e-6 ||
    Math.abs(rootScale.y - 1) > 1e-6 ||
    Math.abs(rootScale.z - 1) > 1e-6;
  if (!isRootScaled) {
    attachEyeMeshes(args);
    return;
  }

  args.rig.root.scale.set(1, 1, 1);
  args.rig.root.updateWorldMatrix(true, true);
  try {
    attachEyeMeshes(args);
  } finally {
    args.rig.root.scale.copy(rootScale);
    args.rig.root.updateWorldMatrix(true, true);
  }
}

/**
 * Pure function: compute the nameplate anchor in rig-local space.
 *
 * Returns a FRESH `THREE.Vector3` (no aliasing). When the rig has a head mesh,
 * the anchor is positioned NAMEPLATE_OFFSET_ABOVE_HEAD above the head's
 * world-space top (head.position.y + head.geometry.boundingBox.max.y). When no
 * head is attached, falls back to bodyMaxY + NAMEPLATE_OFFSET_ABOVE_BODY.
 */
export function computeNameplateAnchor(rig: CharacterRig): THREE.Vector3 {
  const headRoot = rig._headRoot ?? rig._headMesh ?? null;
  if (headRoot !== null) {
    rig.root.updateMatrixWorld(true);
    headRoot.updateMatrixWorld(true);
    const worldBox = new THREE.Box3().setFromObject(headRoot);
    if (!worldBox.isEmpty()) {
      const top = new THREE.Vector3(0, worldBox.max.y + NAMEPLATE_OFFSET_ABOVE_HEAD, 0);
      const center = worldBox.getCenter(new THREE.Vector3());
      top.x = center.x;
      top.z = center.z;
      return rig.root.worldToLocal(top);
    }
  }
  rig.root.updateMatrixWorld(true);
  const worldBox = new THREE.Box3();
  const renderMeshes =
    rig.renderMeshes != null && rig.renderMeshes.length > 0 ? rig.renderMeshes : [rig.bodyMesh];
  for (const mesh of renderMeshes) {
    worldBox.expandByObject(mesh);
  }
  if (!worldBox.isEmpty()) {
    const center = worldBox.getCenter(new THREE.Vector3());
    const top = new THREE.Vector3(center.x, worldBox.max.y + NAMEPLATE_OFFSET_ABOVE_BODY, center.z);
    return rig.root.worldToLocal(top);
  }
  return new THREE.Vector3(0, 1 + NAMEPLATE_OFFSET_ABOVE_BODY, 0);
}

/**
 * Pure function: compute the speech-bubble anchor in rig-local space.
 *
 * Returns a FRESH `THREE.Vector3` (no aliasing). Sits SPEECH_BUBBLE_OFFSET_ABOVE_NAMEPLATE
 * above the nameplate anchor (separate sprite per spec §9 + roadmap line 271 — the emote
 * bubble is NOT the nameplate).
 */
export function computeSpeechBubbleAnchor(rig: CharacterRig): THREE.Vector3 {
  const nameplate = computeNameplateAnchor(rig);
  return new THREE.Vector3(
    nameplate.x,
    nameplate.y + SPEECH_BUBBLE_OFFSET_ABOVE_NAMEPLATE,
    nameplate.z,
  );
}
